import React from 'react';
import { Admin, Resource } from 'react-admin';
import { Route } from 'react-router-dom';
import theme from './theme';
import { QueryClient } from '@tanstack/react-query';
import { GrowthBookDebug } from '../components/debug/GrowthBookDebug';

// Icons
import UserIcon from '@mui/icons-material/Group';
import RoleIcon from '@mui/icons-material/TagFaces';
import VersionIcon from '@mui/icons-material/Book';
import VideocamIcon from '@mui/icons-material/Videocam';
import CodeIcon from '@mui/icons-material/Code';
import OrgIcon from '@mui/icons-material/Domain';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import BlockIcon from '@mui/icons-material/Block';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FeatureIcon from '@mui/icons-material/ToggleOn';
import EventIcon from '@mui/icons-material/Event';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Components
import Dashboard from '../dashboard/dashboard.component';
import UserList from '../users/component/userList.component';
import UserEdit from '../users/component/userEdit.component';
import UserCreate from '../users/component/userCreate.component';
import UserShow from '../users/component/userShow.component';
import VersionsList from '../versions/component/versionsList.component';
import VersionShow from '../versions/component/versionShow.component';
import VersionEdit from '../versions/component/versionEdit.component';
import VersionCreate from '../versions/component/versionCreate.component';
import CodesList from '../codes/component/codesList.component';
import CodeShow from '../codes/component/codeShow.component';
import CodeEdit from '../codes/component/codeEdit.component';
import CodeCreate from '../codes/component/codeCreate.component';
import { OrgsList } from '../organizations/component/orgsList.component';
import OrgShow from '../organizations/component/orgShow.component';
import OrgEdit from '../organizations/component/orgEdit.component';
import OrgCreate from '../organizations/component/orgCreate.component';
import RolesList from '../roles/component/rolesList.component';
import RoleShow from '../roles/component/roleShow.component';
import RolesEdit from '../roles/component/rolesEdit.component';
import RolesCreate from '../roles/component/rolesCreate.component';
import HotlineBlocklistList from '../hotline-blocklist/component/hotlineBlocklistList.component';
import HotlineBlocklistShow from '../hotline-blocklist/component/hotlineBlocklistShow.component';
import HotlineBlocklistEdit from '../hotline-blocklist/component/hotlineBlocklistEdit.component';
import HotlineBlocklistCreate from '../hotline-blocklist/component/hotlineBlocklistCreate.component';
import AuthCallbackComponent from '../auth/authCallback.component';
import UserNotify from '../users/component/userNotify.component';
import ChangePassword from '../users/component/changePassword.component';
import QueueList from '../queues/component/queuesList.component';
import QueueShow from '../queues/component/queuesShow.component';
import Cms1500JobsList from '../cms1500-jobs/component/cms1500JobsList.component';
import Cms1500JobShow from '../cms1500-jobs/component/cms1500JobShow.component';
import { LeadImportList } from '../lead-imports/component/leadImportList.component';
import { LeadImportShow } from '../lead-imports/component/leadImportShow.component';
import { LeadList } from '../leads/component/leadList.component';
import { LeadShow } from '../leads/component/leadShow.component';
import { ProductFeatureList } from '../product-features/productFeatureList.component';
import { ProductFeatureEdit, ProductFeatureCreate } from '../product-features/productFeatureEdit.component';
import AppointmentTypesList from '../appointment-types/component/appointmentTypesList.component';
import AppointmentTypesShow from '../appointment-types/component/appointmentTypesShow.component';
import { AssetsList, AssetShow, AssetEdit, AssetCreate } from '../partner-marketing-assets';

// Providers and Config
import CustomLayout from './customLayout';
import authProvider from '../auth/authProvider';
import dataProvider from './dataProvider';
import LoginComponent from '../auth/login.component';
// import { usePermissions } from '../auth/PermissionsContext';

// Resource Names
import { USERS_RESOURCE_NAME as USERS_RESOURCE } from '../users/user.dataProvider';
import { ROLES_RESOURCE_NAME as ROLES_RESOURCE } from '../roles/roles.dataProvider';
import { PROVIDER_ROLES_RESOURCE_NAME as PROVIDER_ROLES_RESOURCE } from '../roles/provider.roles.dataProvider';
import { NON_PROVIDER_ROLES_RESOURCE_NAME as NON_PROVIDER_ROLES_RESOURCE } from '../roles/non.provider.roles.dataProvider';
import { VERSIONS_RESOURCE_NAME as VERSIONS_RESOURCE } from '../versions/versions.dataProvider';
import { CODES_RESOURCE_NAME as CODES_RESOURCE } from '../codes/codes.dataProvider';
import { ORGANIZATIONS_RESOURCE_NAME as ORGANIZATIONS_RESOURCE } from '../organizations/organizations.dataProvider';
import { ALL_ORGANIZATIONS_RESOURCE_NAME as ALL_ORGANIZATIONS_RESOURCE } from '../organizations/organizations.dataProvider';
import { US_STATES_RESOURCE_NAME as US_STATES_RESOURCE } from '../usStates/usStates.dataProvider';
import { LANGUAGES_RESOURCE_NAME as LANGUAGES_RESOURCE } from '../languages/languages.dataProvider';
import { HOTLINE_BLOCKLIST_RESOURCE_NAME as HOTLINE_BLOCKLIST_RESOURCE } from '../hotline-blocklist/hotlineBlocklist.dataProvider';
import { PERMISSION_TEMPLATES_RESOURCE_NAME as PERMISSION_TEMPLATE_RESOURCE } from '../permissionTemplates/permission.template.dataProvider';
import { PATIENT_RESOURCE_NAME as PATIENT_RESOURCE } from '../users/patient.dataProvider';
import { PROVIDER_RESOURCE_NAME as PROVIDER_RESOURCE } from '../users/provider.dataProvider';
import { ADMIN_RESOURCE_NAME as ADMIN_RESOURCE } from '../users/admin.dataProvider';
import { QUEUES_RESOURCE_NAME as QUEUES_RESOURCE } from '../queues/queues.dataProvider';
import { CMS1500_JOBS_RESOURCE_NAME as CMS1500_JOBS_RESOURCE } from '../cms1500-jobs/cms1500Jobs.dataProvider';
import { INSURANCE_PLANS_RESOURCE_NAME as INSURANCE_PLANS_RESOURCE } from '../insurancePlans/insurance.plans.dataProvider';
import { LEAD_IMPORTS_RESOURCE_NAME as LEAD_IMPORTS_RESOURCE } from '../lead-imports/leadImports.dataProvider';
import { LEADS_RESOURCE_NAME as LEADS_RESOURCE } from '../leads/leads.dataProvider';
import { APPOINTMENT_TYPES_RESOURCE_NAME as APPOINTMENT_TYPES_RESOURCE } from '../appointment-types/appointment-types.model';
import { PARTNER_MARKETING_ASSETS_RESOURCE_NAME as PARTNER_MARKETING_ASSETS_RESOURCE } from '../partner-marketing-assets/partnerMarketingAssets.model';
import { PROVIDER_AVAILABILITY_RESOURCE_NAME as PROVIDER_AVAILABILITY_RESOURCE } from '../availability-schedules/availability-schedules.model';
import AvailabilityCalendarPage from '../availability-schedules/component/AvailabilityCalendarPage';

// Date formatting options
export const DATE_TIME_OPTIONS = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
  hour: '2-digit' as const,
  minute: '2-digit' as const,
  hour12: false,
  timeZone: 'America/Toronto',
} satisfies Intl.DateTimeFormatOptions;

export const DATE_OPTIONS = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
} satisfies Intl.DateTimeFormatOptions;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: false,
    },
  },
});

const App = () => {
  // const { hasPermission } = usePermissions();

  return (
    <>
      <Admin
        loginPage={LoginComponent}
        dataProvider={dataProvider}
        authProvider={authProvider}
        dashboard={Dashboard}
        layout={CustomLayout}
        requireAuth
        basename=""
        theme={theme}
        queryClient={queryClient}
      >
    <Route path="/callback" element={<AuthCallbackComponent />} />
    <Resource
      name={PATIENT_RESOURCE}
      options={{
        label: 'Patients',
        menuLabel: 'Patients',
      }}
      icon={PersonIcon}
      list={UserList}
      show={UserShow}
      edit={UserEdit}
      create={UserCreate}
    >
      <Route path=":id/notify" element={<UserNotify />} />
      <Route path=":id/changePassword" element={<ChangePassword />} />
    </Resource>
    <Resource
      name={PROVIDER_RESOURCE}
      options={{
        label: 'Providers',
        menuLabel: 'Providers',
        subMenu: true,
      }}
      icon={LocalHospitalIcon}
      list={UserList}
      show={UserShow}
      edit={UserEdit}
      create={UserCreate}
    >
      <Route path=":id/notify" element={<UserNotify />} />
      <Route path=":id/changePassword" element={<ChangePassword />} />
    </Resource>
    <Resource
      name={PROVIDER_AVAILABILITY_RESOURCE}
      options={{
        label: 'Availability',
        menuLabel: 'Availability',
        parent: 'Providers',
      }}
      icon={CalendarMonthIcon}
      list={AvailabilityCalendarPage}
    />
    <Resource
      name={ADMIN_RESOURCE}
      options={{
        label: 'Admins',
        menuLabel: 'Admins',
      }}
      icon={AdminPanelSettingsIcon}
      list={UserList}
      show={UserShow}
      edit={UserEdit}
      create={UserCreate}
    >
      <Route path=":id/notify" element={<UserNotify />} />
      <Route path=":id/changePassword" element={<ChangePassword />} />
    </Resource>
    <Resource
      name="product-features"
      options={{
        label: 'Product Features',
        menuLabel: 'Product Features',
      }}
      icon={FeatureIcon}
      list={ProductFeatureList}
      edit={ProductFeatureEdit}
      create={ProductFeatureCreate}
    />
    <Resource
      name={APPOINTMENT_TYPES_RESOURCE}
      icon={EventIcon}
      options={{
        label: 'Appointment Types',
        menuLabel: 'Appointment Types',
      }}
      list={AppointmentTypesList}
      show={AppointmentTypesShow}
    />
    <Resource
      name={QUEUES_RESOURCE}
      icon={VideocamIcon}
      options={{ label: 'Queues' }}
      list={QueueList}
      show={QueueShow}
    />
    <Resource
      name={ROLES_RESOURCE}
      options={{
        label: 'Roles',
      }}
      icon={RoleIcon}
      list={RolesList}
      show={RoleShow}
      edit={RolesEdit}
      create={RolesCreate}
    />
    <Resource
      name={CODES_RESOURCE}
      icon={CodeIcon}
      options={{ label: 'Codes' }}
      list={CodesList}
      show={CodeShow}
      edit={CodeEdit}
      create={CodeCreate}
    />
    <Resource
      name={ORGANIZATIONS_RESOURCE}
      icon={OrgIcon}
      options={{ label: 'Organizations' }}
      list={OrgsList}
      show={OrgShow}
      edit={OrgEdit}
      create={OrgCreate}
    />
    <Resource
      name={LEAD_IMPORTS_RESOURCE}
      icon={UploadFileIcon}
      options={{ label: 'Lead Imports' }}
      list={LeadImportList}
      show={LeadImportShow}
    />
    <Resource
      name={LEADS_RESOURCE}
      icon={PersonSearchIcon}
      options={{ label: 'Leads' }}
      list={LeadList}
      show={LeadShow}
    />
    <Resource
      name={CMS1500_JOBS_RESOURCE}
      icon={AssignmentIcon}
      options={{ label: 'CMS-1500 Jobs' }}
      list={Cms1500JobsList}
      show={Cms1500JobShow}
    />
    <Resource
      name={VERSIONS_RESOURCE}
      icon={VersionIcon}
      options={{ label: 'Versions' }}
      list={VersionsList}
      show={VersionShow}
      edit={VersionEdit}
      create={VersionCreate}
    />
    <Resource
      name={HOTLINE_BLOCKLIST_RESOURCE}
      icon={BlockIcon}
      options={{ label: 'Hotline Blocklist' }}
      list={HotlineBlocklistList}
      show={HotlineBlocklistShow}
      edit={HotlineBlocklistEdit}
      create={HotlineBlocklistCreate}
    />
    <Resource
      name={PARTNER_MARKETING_ASSETS_RESOURCE}
      icon={FolderSharedIcon}
      options={{ label: 'Marketing Assets' }}
      list={AssetsList}
      show={AssetShow}
      edit={AssetEdit}
      create={AssetCreate}
    />

    {/* Non visible resources required for API calls */}
    <Resource name={US_STATES_RESOURCE} />
    <Resource name={PERMISSION_TEMPLATE_RESOURCE} />
    <Resource name={INSURANCE_PLANS_RESOURCE} />
    <Resource name={PROVIDER_ROLES_RESOURCE} />
    <Resource name={NON_PROVIDER_ROLES_RESOURCE} />
    <Resource name={ALL_ORGANIZATIONS_RESOURCE} />
    <Resource name={LANGUAGES_RESOURCE} />
    <Resource name={USERS_RESOURCE} />
      </Admin>

      {/* GrowthBook Debug Panel - only shown in development */}
      <GrowthBookDebug />
    </>
  );
};

export default App;
